// Diff per insiemi sul manifest (recordId+modId) — vedi CLAUDE.md "Vincolo di design — diff per
// insiemi, non per valore". Un semplice "stesso recordId, modId diverso -> aggiorna" non basta:
// gli array del manifest (ragazzi, localita, volantini, carico, rifornimento, todo) possono
// guadagnare o perdere elementi, non solo modificarli — un record che "cambia" spesso viene
// eliminato e ricreato (modId torna a 0/1), quindi il diff deve trattare ogni array come un
// insieme, non come una lista di valori da confrontare posizione per posizione.
//
// Modulo puro, nessuna dipendenza da IndexedDB/rete — riceve due snapshot dello stesso formato
// (manifest precedente in cache, manifest appena scaricato) e produce il piano di cosa
// aggiungere/aggiornare/rimuovere, per ogni livello dell'albero.
import type { Uscita } from './api';

export interface ManifestLeaf {
  recordId: number;
  modId: number;
}

export interface UsclocManifestEntry extends ManifestLeaf {
  pwa_usclocloc: ManifestLeaf[];
}

export interface UscitaManifestTree {
  recordId: number;
  modId: number;
  pwa_carico: ManifestLeaf[];
  pwa_rifornimento: ManifestLeaf[];
  pwa_todo: ManifestLeaf[];
  pwa_uscloc: UsclocManifestEntry[];
  pwa_uscprod: ManifestLeaf[];
}

export interface ManifestEntry {
  mob_usc_id: number;
  recordId: number;
  modId: number;
  manifest: UscitaManifestTree | null;
}

export interface DiffResult<T extends ManifestLeaf> {
  added: T[];
  changed: T[];
  removed: T[];
  unchanged: T[];
}

// Primitiva riusabile a ogni livello dell'albero: confronta due liste di {recordId, modId} per
// insieme (per recordId), non per posizione. Un recordId assente in "current" è sempre "removed",
// anche se "previous" non aveva mai visto quell'array (in quel caso previous è semplicemente []).
export function diffManifestList<T extends ManifestLeaf>(previous: T[], current: T[]): DiffResult<T> {
  const prevMap = new Map(previous.map((item) => [item.recordId, item]));
  const currMap = new Map(current.map((item) => [item.recordId, item]));

  const added: T[] = [];
  const changed: T[] = [];
  const unchanged: T[] = [];

  currMap.forEach((currItem, recordId) => {
    const prevItem = prevMap.get(recordId);
    if (!prevItem) {
      added.push(currItem);
    } else if (prevItem.modId !== currItem.modId) {
      changed.push(currItem);
    } else {
      unchanged.push(currItem);
    }
  });

  const removed: T[] = [];
  prevMap.forEach((prevItem, recordId) => {
    if (!currMap.has(recordId)) {
      removed.push(prevItem);
    }
  });

  return { added, changed, removed, unchanged };
}

// Piano d'azione appiattito per tabella FM — indipendente dalla profondità di annidamento
// dell'albero. È quello che serve al passo successivo (fetch mirato): sapere, per ogni tabella,
// quali recordId scaricare (added+changed) e quali eliminare dalla cache (removed).
export interface ManifestDiffPlan {
  uscite: DiffResult<ManifestEntry & ManifestLeaf>;
  uscloc: DiffResult<UsclocManifestEntry>;
  usclocloc: DiffResult<ManifestLeaf>;
  uscprod: DiffResult<ManifestLeaf>;
  carico: DiffResult<ManifestLeaf>;
  rifornimento: DiffResult<ManifestLeaf>;
  todo: DiffResult<ManifestLeaf>;
}

const emptyTree = (): UscitaManifestTree => ({
  recordId: 0,
  modId: 0,
  pwa_carico: [],
  pwa_rifornimento: [],
  pwa_todo: [],
  pwa_uscloc: [],
  pwa_uscprod: [],
});

function mergeDiff<T extends ManifestLeaf>(into: DiffResult<T>, from: DiffResult<T>): void {
  into.added.push(...from.added);
  into.changed.push(...from.changed);
  into.removed.push(...from.removed);
  into.unchanged.push(...from.unchanged);
}

const emptyResult = <T extends ManifestLeaf>(): DiffResult<T> => ({
  added: [],
  changed: [],
  removed: [],
  unchanged: [],
});

// Diff completo di due snapshot manifest (uno per ogni uscita del range, con l'albero annidato
// gia' parsato da JSON). Applica diffManifestList al livello uscite, poi — solo per le uscite
// presenti in entrambi gli snapshot (added, changed o unchanged: non serve annidare in "removed",
// vedi sotto) — ricorsivamente per ogni array figlio, incluso pwa_usclocloc dentro ogni uscloc.
//
// Le uscite "removed" (sparite dal manifest fresco) contribuiscono comunque i loro figli come
// "removed" — presi dal loro albero precedente in cache, dato che il manifest fresco non li
// contiene piu'. Le uscite "added" (nuove) contribuiscono i loro figli come "added" — l'albero
// precedente per quella uscita e' per definizione vuoto.
export function diffManifestTree(previous: ManifestEntry[], current: ManifestEntry[]): ManifestDiffPlan {
  const uscite = diffManifestList<ManifestEntry & ManifestLeaf>(
    previous as (ManifestEntry & ManifestLeaf)[],
    current as (ManifestEntry & ManifestLeaf)[]
  );

  const plan: ManifestDiffPlan = {
    uscite,
    uscloc: emptyResult(),
    usclocloc: emptyResult(),
    uscprod: emptyResult(),
    carico: emptyResult(),
    rifornimento: emptyResult(),
    todo: emptyResult(),
  };

  const applyUscitaChildren = (prevTree: UscitaManifestTree, currTree: UscitaManifestTree) => {
    const uslocDiff = diffManifestList(prevTree.pwa_uscloc, currTree.pwa_uscloc);
    mergeDiff(plan.uscloc, uslocDiff);
    mergeDiff(plan.uscprod, diffManifestList(prevTree.pwa_uscprod, currTree.pwa_uscprod));
    mergeDiff(plan.carico, diffManifestList(prevTree.pwa_carico, currTree.pwa_carico));
    mergeDiff(plan.rifornimento, diffManifestList(prevTree.pwa_rifornimento, currTree.pwa_rifornimento));
    mergeDiff(plan.todo, diffManifestList(prevTree.pwa_todo, currTree.pwa_todo));

    // pwa_usclocloc va confrontato per singola localita' (stesso recordId di uscloc su entrambi i
    // lati), non concatenando tutte le localita' insieme — altrimenti un volantino spostato da una
    // zona all'altra nella stessa uscita (stesso recordId, ricreato) sparirebbe dal diff invece di
    // risultare "removed" dalla vecchia zona e "added" nella nuova.
    const prevUsclocByRecordId = new Map(prevTree.pwa_uscloc.map((u) => [u.recordId, u]));
    const currUsclocByRecordId = new Map(currTree.pwa_uscloc.map((u) => [u.recordId, u]));
    const allUsclocRecordIds = new Set([...prevUsclocByRecordId.keys(), ...currUsclocByRecordId.keys()]);
    allUsclocRecordIds.forEach((recordId) => {
      const prevVolantini = prevUsclocByRecordId.get(recordId)?.pwa_usclocloc || [];
      const currVolantini = currUsclocByRecordId.get(recordId)?.pwa_usclocloc || [];
      mergeDiff(plan.usclocloc, diffManifestList(prevVolantini, currVolantini));
    });
  };

  const prevByUscId = new Map(previous.map((u) => [u.mob_usc_id, u]));
  const currByUscId = new Map(current.map((u) => [u.mob_usc_id, u]));

  // Uscite presenti in entrambi (changed o unchanged): diff reale dei figli.
  [...uscite.changed, ...uscite.unchanged].forEach((entry) => {
    const prevEntry = prevByUscId.get(entry.mob_usc_id);
    const currEntry = currByUscId.get(entry.mob_usc_id);
    applyUscitaChildren(prevEntry?.manifest || emptyTree(), currEntry?.manifest || emptyTree());
  });

  // Uscite nuove: tutti i figli sono "added" (prevTree vuoto per definizione).
  uscite.added.forEach((entry) => {
    const currEntry = currByUscId.get(entry.mob_usc_id);
    applyUscitaChildren(emptyTree(), currEntry?.manifest || emptyTree());
  });

  // Uscite sparite: tutti i figli (dall'ultimo albero noto in cache) sono "removed".
  uscite.removed.forEach((entry) => {
    const prevEntry = prevByUscId.get(entry.mob_usc_id);
    applyUscitaChildren(prevEntry?.manifest || emptyTree(), emptyTree());
  });

  return plan;
}

// Ricostruisce il manifest leggero (recordId+modId annidati) direttamente dalla risposta completa
// di get-flat — nessuna chiamata /manifest separata necessaria per "fotografare" lo stato iniziale
// dopo il primo caricamento: get-flat porta già recordId+modId su ogni entità (vedi
// server/src/services/uscitaAssembler.ts), qui si tratta solo di reimpacchettarli nella stessa
// forma ad albero usata da diffManifestTree/manifestCache.ts.
//
// Nota: il manifest reale (pwa_manifest, calcolato lato FM) mette capogruppo e ragazzi nello
// stesso array pwa_uscprod (la tabella FM è una sola) — qui li rimergiamo per restare confrontabili
// con un manifest scaricato in un secondo momento da /api/uscite/manifest.
export function extractManifestFromUscite(uscite: Uscita[]): ManifestEntry[] {
  return uscite.map((u): ManifestEntry => {
    const pwa_uscprod: ManifestLeaf[] = [
      ...(u.capogruppo || []).map((r) => ({ recordId: r.recordId, modId: r.modId || 0 })),
      ...(u.ragazzi || []).map((r) => ({ recordId: r.recordId, modId: r.modId || 0 })),
    ];

    const pwa_uscloc: UsclocManifestEntry[] = (u.localita || []).map((loc) => ({
      recordId: loc.recordId || 0,
      modId: loc.modId || 0,
      pwa_usclocloc: (loc.properties.volantini || []).map((v) => ({
        recordId: v.recordId || 0,
        modId: v.modId || 0,
      })),
    }));

    const pwa_carico: ManifestLeaf[] = (u.carico || []).map((c) => ({ recordId: c.recordId, modId: c.modId || 0 }));
    const pwa_rifornimento: ManifestLeaf[] = (u.rifornimento || []).map((r) => ({
      recordId: r.recordId,
      modId: r.modId || 0,
    }));

    return {
      mob_usc_id: u.mob_usc_id,
      recordId: u.recordId,
      modId: u.modId || 0,
      manifest: {
        recordId: u.recordId,
        modId: u.modId || 0,
        pwa_carico,
        pwa_rifornimento,
        pwa_todo: [], // mai popolato lato client (vedi CLAUDE.md, get-flat lo omette deliberatamente)
        pwa_uscloc,
        pwa_uscprod,
      },
    };
  });
}
